import { FunctionComponent } from 'react';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import classnames from 'classnames';
import get from 'lodash.get';
// @ts-ignore missing type declarations
import { Swiper } from 'dibs-pinch-zoom-swipe';

import styles from '../styles.scss';
import { ThumbImage, ImageProps } from './ThumbImage';

import { ProductSwiper_item as ProductSwiperProp } from './__generated__/ProductSwiper_item.graphql';

type Props = {
    item: ProductSwiperProp;
    wasHovered: boolean;
    swiperWasClicked: boolean;
    isInitiallyVisibleTile: boolean;
    arrowsVisible: boolean;
    isMobile: boolean;
    itemsPerRow: number;
    index: number;
    onPageChange: (nextPage: number) => void;
    imageProps: ImageProps;
    onSwipeStart?: () => void;
    onSwipeEnd?: () => void;
};

const ProductSwiperComponent: FunctionComponent<Props> = props => {
    const {
        item,
        wasHovered,
        swiperWasClicked,
        isInitiallyVisibleTile,
        arrowsVisible,
        isMobile,
        index: tileIndex,
        onPageChange,
        imageProps,
        onSwipeStart,
        onSwipeEnd,
    } = props;

    const carouselPhotos = get(item, 'carouselPhotos') || [];
    const hasSingleImage = carouselPhotos.length === 1;

    const imageContainerClassNames = classnames(
        hasSingleImage ? styles.imageContainer : styles.swiperImageContainer,
        styles[imageProps.imageSize]
    );

    const images = carouselPhotos.map(
        (photo, index) =>
            photo && (
                <ThumbImage
                    {...imageProps}
                    useEagerImageLoad={
                        (isInitiallyVisibleTile && index === 0) || wasHovered || swiperWasClicked
                    }
                    photo={photo}
                    index={index}
                    key={`product-thumb-image-${tileIndex}-${index}`}
                    containerClassName={imageContainerClassNames}
                />
            )
    );

    if (hasSingleImage) return images[0];

    return (
        <Swiper
            itemsPerPage={1}
            showArrows={!isMobile}
            hasInfiniteLoop
            onPageChange={onPageChange}
            classNames={{
                item: styles.swiperImageListItem,
                prevArrow: [
                    styles.bumpSpecificity,
                    styles.swiperArrow,
                    styles.leftArrow,
                    arrowsVisible ? styles.arrowVisible : null,
                ],
                nextArrow: [
                    styles.bumpSpecificity,
                    styles.swiperArrow,
                    styles.rightArrow,
                    arrowsVisible ? styles.arrowVisible : null,
                ],
            }}
            onSwipeStart={onSwipeStart}
            onSwipeEnd={onSwipeEnd}
        >
            {images}
        </Swiper>
    );
};

export const ProductSwiper = createFragmentContainer(ProductSwiperComponent, {
    item: graphql`
        fragment ProductSwiper_item on Item
        @argumentDefinitions(photosLimit: { type: "Int", defaultValue: 3 }) {
            carouselPhotos: photos(limit: $photosLimit) {
                ...ThumbImage_photo
            }
        }
    `,
});
